import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  LayoutDashboard, Search, Activity, RefreshCw, Plus, Hand, CheckCircle2,
  X, Truck, Fuel, Zap, MapPin, HardHat, Clock, Send, ChevronRight,
  BarChart3, Users, Timer, MessageSquare, AlertTriangle, Trash2,
  ArrowLeft, LogOut, Loader2, ChevronDown, Sparkles, Coffee
} from "lucide-react";
import LiveFeed from "@/components/shared/LiveFeed";

const REGIONS = ["Antwerpen", "Brussel", "Oost-Vlaanderen", "West-Vlaanderen", "Wallonie"];

export default function PlannerDashboard() {
  const { session, hubData, refreshData, logout, loading } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { sites, machines, drivers, requests, events } = hubData;

  const [activeRegion, setActiveRegion] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [reviewDriver, setReviewDriver] = useState(null);
  const [pingMessage, setPingMessage] = useState("");
  const [expandedSite, setExpandedSite] = useState(null);
  const [funMode, setFunMode] = useState(false);
  const [funText, setFunText] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    showToast(t("data_synced"));
    setIsRefreshing(false);
  };

  const handleHighFive = async (to) => {
    await api.sendHighFive({ from_user: session.userName || "Planner", to_user: to, hub_id: session.hubId });
    showToast(t("high_five_sent"));
    refreshData();
  };

  const handleSendPing = async (driverName) => {
    if (!pingMessage.trim()) return;
    await api.sendMessage({
      hub_id: session.hubId, from_user: session.userName || "Planner",
      to_user: driverName, message: pingMessage, msg_type: "PING"
    });
    showToast(t("ping_sent"));
    setPingMessage("");
    refreshData();
  };

  const handleApprove = async (reqId, driverName) => {
    await api.approveRequest(reqId, driverName);
    showToast(t("approve") + "!");
    refreshData();
  };

  const handleRemoveDriver = async (name) => {
    await api.deleteDriver(name, session.hubId);
    showToast(`${name} removed`);
    setReviewDriver(null);
    refreshData();
  };

  const handleFunRequest = async () => {
    if (!funText.trim()) return;
    await api.createRequest({
      hub_id: session.hubId, machine_id: "FUN", machine_number: "FUN-001",
      site_id: sites[0]?.id || "FUN", site_name: "Fun Request",
      urgency: "NONE", products: ["FUN"], notes: funText,
      requester_type: "PLANNER", is_fun_request: true, fun_message: funText
    });
    showToast("Fun request sent!");
    setFunText("");
    setFunMode(false);
    refreshData();
  };

  const driverStats = useMemo(() => {
    const stats = {};
    drivers.forEach(d => {
      const dReqs = requests.filter(r => r.assigned_driver === d.driver_name && r.status === "DONE");
      const total = requests.filter(r => r.assigned_driver === d.driver_name).length;
      stats[d.driver_name] = {
        liters: dReqs.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
        adblue: dReqs.reduce((s, r) => s + (r.delivered_adblue || 0), 0),
        machines: dReqs.length,
        sites: new Set(dReqs.map(r => r.site_id)).size,
        efficiency: total > 0 ? Math.round((dReqs.length / total) * 100) : 0,
        tasks: dReqs.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
      };
    });
    return stats;
  }, [drivers, requests]);

  const globalStats = useMemo(() => {
    const done = requests.filter(r => r.status === "DONE");
    return {
      totalLiters: done.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
      totalMachines: done.length,
      activeDrivers: drivers.filter(d => d.status === "ACTIVE").length,
      newRequests: requests.filter(r => r.status === "NEW").length
    };
  }, [requests, drivers]);

  const filteredSites = useMemo(() => {
    return sites.filter(s => {
      const matchRegion = activeRegion === "ALL" || s.region === activeRegion;
      const matchSearch = (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.city || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchRegion && matchSearch;
    });
  }, [sites, activeRegion, searchQuery]);

  const getSiteRequests = (siteId) => requests.filter(r => r.site_id === siteId);
  const getSiteMachines = (siteId) => machines.filter(m => m.site_id === siteId);

  return (
    <div className="min-h-[100dvh] bg-slate-950 flex flex-col" data-testid="planner-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1000] bg-lime-500 text-slate-950 px-6 py-3 rounded-md shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Driver Review Modal */}
      {reviewDriver && (
        <div className="fixed inset-0 z-[200] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" data-testid="driver-review-modal">
          <div className="bg-slate-900 rounded-md w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-800 animate-scaleIn">
            <header className="bg-slate-950 p-6 flex justify-between items-center border-b border-slate-800">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-md flex items-center justify-center text-white font-black text-xl ${reviewDriver.status === "ACTIVE" ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                  {reviewDriver.driver_name[0]}
                </div>
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight text-slate-100">{reviewDriver.driver_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`status-dot ${reviewDriver.status === "ACTIVE" ? "online" : "offline"}`} />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{reviewDriver.region}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleHighFive(reviewDriver.driver_name)} className="p-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-md transition-all active:scale-95" data-testid="modal-highfive-btn">
                  <Hand className="w-5 h-5" />
                </button>
                <button onClick={() => setReviewDriver(null)} className="p-3 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-md transition-all" data-testid="close-driver-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </header>
            <div className="p-6 overflow-y-auto max-h-[70vh] grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-lime-500" /> Performance
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-md">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Diesel</span>
                    <span className="text-xl font-black text-slate-100 font-mono">{(driverStats[reviewDriver.driver_name]?.liters || 0)}L</span>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-md">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">AdBlue</span>
                    <span className="text-xl font-black text-slate-100 font-mono">{(driverStats[reviewDriver.driver_name]?.adblue || 0)}L</span>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-md">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">{t("efficiency")}</span>
                    <span className="text-xl font-black text-lime-400 font-mono">{(driverStats[reviewDriver.driver_name]?.efficiency || 0)}%</span>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-md">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">{t("stops")}</span>
                    <span className="text-xl font-black text-slate-100 font-mono">{(driverStats[reviewDriver.driver_name]?.sites || 0)}</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-800 space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-lime-500" /> {t("direct_communication")}
                  </h4>
                  <div className="relative">
                    <textarea data-testid="ping-message-input" value={pingMessage} onChange={e => setPingMessage(e.target.value)}
                      placeholder={t("send_message_placeholder")}
                      className="w-full p-4 bg-slate-950 border border-slate-800 rounded-md text-sm text-slate-100 h-28 outline-none focus:border-lime-500/50 resize-none font-medium placeholder:text-slate-600" />
                    <button data-testid="send-ping-btn" onClick={() => handleSendPing(reviewDriver.driver_name)} disabled={!pingMessage.trim()}
                      className="absolute bottom-3 right-3 p-3 bg-lime-500 text-slate-950 rounded-md shadow-lg disabled:opacity-30 active:scale-90 transition-all">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                  <Clock className="w-4 h-4 text-lime-500" /> Route History
                </h4>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {(driverStats[reviewDriver.driver_name]?.tasks || []).length === 0 ? (
                    <p className="text-xs text-slate-600 text-center py-8 font-bold uppercase tracking-wider">{t("no_stops_yet")}</p>
                  ) : (
                    (driverStats[reviewDriver.driver_name]?.tasks || []).slice(0, 8).map((task, i) => (
                      <div key={i} className="p-3 bg-slate-950 border border-slate-800 rounded-md">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-bold text-slate-300 uppercase">{task.site_name || "Site"}</span>
                          <span className="text-[10px] font-mono text-slate-500">{task.completed_at ? new Date(task.completed_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ""}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-[10px] font-bold bg-slate-800 text-slate-400 px-2 py-0.5 rounded">{task.machine_number}</span>
                          <span className="text-[10px] font-bold bg-lime-500/10 text-lime-400 px-2 py-0.5 rounded">+{task.delivered_diesel || 0}L</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <button data-testid="remove-driver-btn" onClick={() => handleRemoveDriver(reviewDriver.driver_name)}
                  className="w-full py-3 bg-red-600/10 border border-red-600/20 text-red-500 rounded-md text-xs font-bold uppercase tracking-wider hover:bg-red-600 hover:text-white transition-all flex items-center justify-center gap-2">
                  <Trash2 className="w-4 h-4" /> Remove Driver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-950 px-4 md:px-8 py-4 flex items-center justify-between border-b border-slate-800 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-lime-500 rounded-md flex items-center justify-center text-slate-950 shadow-[0_0_15px_rgba(132,204,22,0.3)]">
            <LayoutDashboard className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-100">{t("planner_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500">{session?.hubName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="fun-request-btn" onClick={() => setFunMode(!funMode)}
            className={`p-2.5 rounded-md transition-all text-xs font-bold ${funMode ? "bg-amber-500 text-slate-950" : "bg-slate-800 border border-slate-700 text-slate-400 hover:text-white"}`}>
            <Coffee className="w-4 h-4" />
          </button>
          <button data-testid="planner-refresh-btn" onClick={handleRefresh}
            className={`p-2.5 bg-slate-800 border border-slate-700 rounded-md text-slate-400 hover:text-white transition-all ${isRefreshing ? "animate-spin-slow" : ""}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button data-testid="planner-exit-btn" onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-600/10 border border-red-600/20 rounded-md text-red-500 hover:bg-red-600 hover:text-white transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Fun Request Bar */}
      {funMode && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 md:px-8 py-3 flex items-center gap-3 animate-slideInDown">
          <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
          <input data-testid="fun-request-input" value={funText} onChange={e => setFunText(e.target.value)}
            placeholder={t("fun_placeholder")} className="flex-1 bg-transparent text-sm text-slate-100 font-bold placeholder:text-amber-500/50 outline-none" />
          <button data-testid="send-fun-request-btn" onClick={handleFunRequest} disabled={!funText.trim()}
            className="px-4 py-2 bg-amber-500 text-slate-950 rounded-md text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all">
            Send
          </button>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 p-4 md:p-8 grid grid-cols-1 lg:grid-cols-4 gap-6 overflow-hidden">
        {/* Left: Sites */}
        <div className="lg:col-span-3 space-y-5 overflow-y-auto pr-1 max-h-[calc(100vh-160px)]">
          <LiveFeed events={events} />

          {/* Region Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            <button data-testid="region-all" onClick={() => setActiveRegion("ALL")}
              className={`px-4 py-2.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === "ALL" ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-900 text-slate-500 border-slate-800 hover:border-slate-600"}`}>
              {t("all_regions")}
            </button>
            {REGIONS.map(r => (
              <button key={r} data-testid={`region-${r}`} onClick={() => setActiveRegion(r)}
                className={`px-4 py-2.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${activeRegion === r ? "bg-lime-500 text-slate-950 border-lime-500" : "bg-slate-900 text-slate-500 border-slate-800 hover:border-slate-600"}`}>
                {r}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
            <input data-testid="planner-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full pl-11 pr-4 py-3.5 bg-slate-900 border border-slate-800 rounded-md text-sm font-medium text-slate-100 placeholder:text-slate-600 focus:border-lime-500/40 outline-none transition-all" />
          </div>

          {/* Sites List */}
          <div className="space-y-3">
            {filteredSites.map((site, i) => {
              const sReqs = getSiteRequests(site.id);
              const sMachines = getSiteMachines(site.id);
              const hasUrgent = sReqs.some(r => r.urgency === "URGENT" && r.status !== "DONE");
              const newReqs = sReqs.filter(r => r.status === "NEW");
              const isExpanded = expandedSite === site.id;

              return (
                <div key={site.id} data-testid={`site-row-${site.id}`}
                  className={`bg-slate-900 border rounded-md overflow-hidden transition-all animate-fadeInUp ${hasUrgent ? "border-red-600/40" : "border-slate-800 hover:border-slate-600"}`}
                  style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }}>
                  <button onClick={() => setExpandedSite(isExpanded ? null : site.id)}
                    className="w-full p-4 flex items-center justify-between text-left">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${hasUrgent ? "bg-red-600/10 text-red-500" : "bg-slate-800 text-slate-400"}`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight">{site.name}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{site.city} / {site.region}</span>
                          {site.main_driver && (
                            <span className="text-[10px] font-bold bg-slate-800 text-slate-400 px-2 py-0.5 rounded">{site.main_driver}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {newReqs.length > 0 && (
                        <span className="px-2 py-1 bg-lime-500/10 text-lime-400 rounded text-[10px] font-bold">{newReqs.length} NEW</span>
                      )}
                      {hasUrgent && (
                        <span className="px-2 py-1 bg-red-600/10 text-red-500 rounded text-[10px] font-bold animate-pulse">URGENT</span>
                      )}
                      <span className="text-[10px] font-mono text-slate-500">{sMachines.length} machines</span>
                      <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-slate-800 p-4 space-y-3 animate-fadeIn">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-3 bg-slate-950 rounded-md">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">{t("new_requests")}</span>
                          <span className="text-lg font-black text-lime-400 font-mono">{sReqs.filter(r => r.status === "NEW").length}</span>
                        </div>
                        <div className="p-3 bg-slate-950 rounded-md">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">{t("in_progress")}</span>
                          <span className="text-lg font-black text-amber-400 font-mono">{sReqs.filter(r => r.status === "IN_PROGRESS").length}</span>
                        </div>
                        <div className="p-3 bg-slate-950 rounded-md">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block">{t("completed")}</span>
                          <span className="text-lg font-black text-slate-100 font-mono">{sReqs.filter(r => r.status === "DONE").length}</span>
                        </div>
                      </div>
                      {newReqs.map(req => (
                        <div key={req.id} className="p-3 bg-slate-950 border border-slate-800 rounded-md flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-slate-200">{req.machine_number}</span>
                            <span className="text-[10px] text-slate-500 ml-2">{req.is_fun_request ? `Fun: ${req.fun_message}` : req.notes}</span>
                          </div>
                          <button data-testid={`approve-${req.id}`} onClick={() => handleApprove(req.id, site.main_driver)}
                            className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-xs font-bold uppercase active:scale-95 transition-all">
                            {t("approve")}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Fleet Intelligence */}
        <aside className="hidden lg:flex flex-col space-y-5 overflow-y-auto max-h-[calc(100vh-160px)] pr-1">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 px-1">
            <Activity className="w-4 h-4 text-lime-500" /> {t("fleet_intelligence")}
          </div>

          {/* Global Stats */}
          <div className="p-5 bg-slate-900 border border-slate-800 rounded-md space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-lime-500/10 rounded-md"><BarChart3 className="w-4 h-4 text-lime-500" /></div>
              <div>
                <span className="text-[10px] font-bold text-lime-500 uppercase tracking-wider block">{t("fleet_volume")}</span>
                <span className="text-xl font-black font-mono text-slate-100">{globalStats.totalLiters}L</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <span className="flex items-center gap-1"><Users className="w-3 h-3 text-lime-500" /> {globalStats.activeDrivers} {t("active_drivers")}</span>
              <span className="flex items-center gap-1"><HardHat className="w-3 h-3 text-amber-500" /> {globalStats.totalMachines} {t("refuelings")}</span>
            </div>
            {globalStats.newRequests > 0 && (
              <div className="p-2 bg-lime-500/10 border border-lime-500/20 rounded text-center">
                <span className="text-xs font-bold text-lime-400">{globalStats.newRequests} {t("new_requests")}</span>
              </div>
            )}
          </div>

          {/* Driver Cards */}
          {drivers.map(driver => {
            const stats = driverStats[driver.driver_name] || { liters: 0, machines: 0, sites: 0 };
            const isActive = driver.status === "ACTIVE";
            return (
              <div key={driver.driver_name} data-testid={`driver-card-${driver.driver_name}`}
                onClick={() => setReviewDriver(driver)}
                className="bg-slate-900 border border-slate-800 rounded-md p-5 cursor-pointer hover:border-slate-600 transition-all group">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-md flex items-center justify-center text-white font-black text-lg ${isActive ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                      {driver.driver_name[0]}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-100 uppercase tracking-tight">{driver.driver_name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className={`status-dot ${isActive ? "online" : "offline"}`} />
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{driver.region}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-amber-500">
                    <Hand className="w-3.5 h-3.5" />
                    <span className="text-xs font-black">{driver.high_five_count || 0}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-2 bg-slate-950 rounded text-center">
                    <span className="text-[9px] font-bold text-slate-500 uppercase block">Diesel</span>
                    <span className="text-sm font-black text-slate-100 font-mono">{stats.liters}L</span>
                  </div>
                  <div className="p-2 bg-slate-950 rounded text-center">
                    <span className="text-[9px] font-bold text-slate-500 uppercase block">{t("machines")}</span>
                    <span className="text-sm font-black text-slate-100 font-mono">{stats.machines}</span>
                  </div>
                </div>
                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setReviewDriver(driver)}
                    className="flex-1 py-2 bg-slate-950 text-slate-300 rounded text-[10px] font-bold uppercase tracking-wider hover:bg-slate-800 flex items-center justify-center gap-1 transition-all">
                    <Activity className="w-3 h-3 text-lime-500" /> {t("analyze")}
                  </button>
                  <button onClick={() => handleHighFive(driver.driver_name)}
                    className="p-2 bg-amber-500/10 text-amber-500 rounded hover:bg-amber-500 hover:text-slate-950 transition-all active:scale-90" data-testid={`highfive-${driver.driver_name}`}>
                    <Hand className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </aside>
      </main>
    </div>
  );
}
